import React, {FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject, Fragment}  from 'react';

import {useParams} from 'react-router';
import { useNavigate } from 'react-router-dom';
import { SnackbarAction, SnackbarKey, useSnackbar } from 'notistack';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation  } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import BoltIcon from '@mui/icons-material/Bolt';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import InsightsIcon from '@mui/icons-material/Insights';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';

import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';


import { IEntity, IResult } from 'library/interface';

import useStockMovementService, { useBasicFilterStockMovement } from './services/StockMovement';

import useStoreService from './services/Store';

import {  IStockMovement, IStockMovementArticle, MovementType, defaultStockMovement,defaultStockMovementArticle } from './models/StockMovement';

import useEntityService, {useBasicFilterEntity, useBasicFilterFeatureDescription} from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom, currentUserSessionAtom, isSearchBoxShowAtom, isSaveLoadingAtom } from 'library/store';

import externalLibraryService from 'features/configuration/services/ExternalLibrary';
import {defaultExternalLibrary, defaultExternalLibraryClass, IExternalLibrary, IExternalLibraryClass} from 'features/configuration/models/ExternalLibrary';

import { useBasicFilterProduct } from 'features/setup/services/Product';

import { carouselImage, justifyCenter, typographyGroupBoxStyling } from 'themes/commonStyles';

import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';
import { isFalsy } from 'utility-types';
import { FormDialog } from 'components/ui/FormDialog';
import { Checkbox, Chip, FormControlLabel, IconButton, InputAdornment, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import IEnumeration, {Enum_OPERATION_STATUS, Enum_ARTICLE_OPTION, Enum_STOCK_TRANSFER_STATUS_PURPOSE, IEnumerationItem, Enum_STOCK_MOVEMENT_PURPOSE_IN, Enum_STOCK_MOVEMENT_PURPOSE_OUT} from 'features/configuration/models/Enumeration';
import useEnumerationService, { useBasicFilterEnumeration } from 'features/configuration/services/Enumeration';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import { DatePicker } from '@mui/x-date-pickers';
import EnhancedTable from 'components/ui/EnhancedTable';
import usePersonService, { useBasicFilterPerson, useBasicFilterPersonSearch } from 'features/production/services/Person';

import { IPerson, PersonType } from 'features/production/models/Person';
import { defaultProduct, IProduct, IProductOption } from 'features/setup/models/Product';
import NumberFormat, { NumberFormatValues } from 'react-number-format';
import TextFieldRight from 'components/ui/TextFieldRight';
import { GrClose, GrSearch } from 'react-icons/gr';
import { debounce, sum } from 'lodash';
import { MdOutlineAdd } from 'react-icons/md';
import { IStore } from './models/Store';
import BasicButtonList from 'components/ui/BasicButtonList';

export const StockMovementForm: FC<IStockMovement> = (props: IStockMovement = defaultStockMovement) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {id} = useParams();
  
  const [_id, _setId] = useState<number>( Number( id || 0 ) );

  const {language: lg, roleEntities} = useRecoilValue(currentUserSessionAtom);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const {retrieveEntity, retrieveData, openEntityActionDrawer, openEntityPrintDrawer, checkEntitySaveAuthorization} = useEntityService();
  const {createStockMovement, updateStockMovement } = useStockMovementService();

  const {getStores } = useStoreService();

  const {getEnumerationItemsByEnumerationCodes, getAsOptions} = useEnumerationService();

  const { getPersonsSearchCount } = usePersonService();


  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterStockMovement = useBasicFilterStockMovement( 
    
    async (event: React.MouseEvent<unknown>, row: IStockMovement) => {
      const {id, reference } = row;
      
      setIsSearchBoxShow(false);
      _setId(row.id);
    }
  );

  const refArticleIndex = useRef<number>(0);

  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration( 
      (event: React.MouseEvent<unknown>, row: IEnumeration) => {
          const {id, name, description} = row;
          
          // setParameterEnumeration(id, name);                                      
          // setOpenEnumerationFilter(false);
      }
  );

  const [currentProductOptions, setCurrentProductOptions] = useState<IProductOption[]>([]);
  
  const [showProductOption, setShowProductOption] = useState<boolean>(false);

  const [currentArticle2Add, setCurrentArticle2Add] = useState<IProduct>({...defaultProduct});
  const [openProductFilter, setOpenProductFilter] = useState(false);
  const basicFilterProduct = useBasicFilterProduct( 2,
      (event: React.MouseEvent<unknown>, row: IProduct) => {
          const {id, name, type, description, filterOption, productOptions} = row;

          if( type !== 'article') return;

          setCurrentArticle2Add(row);
          setCurrentProductOptions(productOptions);
          setShowProductOption(true); 
                           
          setOpenProductFilter(false);
      }
  );

  

  
  const emptyFunc = (obj: any) => {}

  const methods = useForm<IStockMovement>({defaultValues:defaultStockMovement});
  const { register, setValue ,getValues, watch, reset ,handleSubmit ,control , formState: { errors } } = methods;

  const watchType = watch('type');

  const [stockMovementArticleIndex, setStockMovementArticleIndex] = useState<number>(-1);

  type SearchPersonUsage = 'supplier';
  const [currentSearchPersonUsage, setCurrentSearchPersonUsage] = useState<SearchPersonUsage>('supplier');
    

  const queryClient = useQueryClient();
  const {isLoading, isError, isSuccess ,error,mutate } = useMutation<IResult<IStockMovement>,Error,IStockMovement>(
      _id>0?updateStockMovement:createStockMovement, {   
        onSuccess: (data: IResult<IStockMovement>) => {
          enqueueSnackbar( t('Operation done !!!'), { variant: 'success',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 }); 
                   
          //reset(data.data);
          setIsSaveLoading(false);
          _setId(data.data.id);
          //setCurrentEntityIdForAction(data.data.id);
          setCurrentFormNameAtom(`${t('StockMovement')} - # ${data.data.id} # ` );
          //queryClient.invalidateQueries(['StockMovement',data.data.id]);
        },
        onError: (err: Error) => {          
          enqueueSnackbar( error?.message, { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
          setIsSaveLoading(false);
        }
      });

    
    const {data: _data, refetch} = useQuery<IStockMovement>(['StockMovement', _id], () => retrieveEntity('StockMovement',_id), 
      {refetchOnWindowFocus: false ,enabled: false } );

    const {data: enumItems} = useQuery<IEnumerationItem[]>( ['EnumerationItems', 'StockMovement'], () => getEnumerationItemsByEnumerationCodes
      ( [Enum_OPERATION_STATUS, Enum_ARTICLE_OPTION, Enum_STOCK_TRANSFER_STATUS_PURPOSE, 
        Enum_STOCK_MOVEMENT_PURPOSE_IN, Enum_STOCK_MOVEMENT_PURPOSE_OUT ] ));

    const {data: stores} = useQuery<IStore[]>( ['stores'], () => getStores( { name: '', description: '', location: ''} ));


  const cellEditableArticle = (row: IStockMovementArticle, cellId: keyof IStockMovementArticle) => {
    return true;
  }

  const [openStockMovementArticle, setOpenStockMovementArticle] = useState<boolean>(false);

  
  const getInputAdornmentArticle = (row: IStockMovementArticle, cellId: keyof IStockMovementArticle)  => ({

    toolTip: `${t('Details')} ...`,
    icon: MoreVertIcon,
    //iconDisable: row.resourceId > 0,
    onClickIcon: (event: any, index: number, row: IStockMovementArticle ) => {
      //refArticleIndex.current = index;
      setStockMovementArticleIndex(index);
      setOpenStockMovementArticle(true);
    }  
  })

 const getArticleOptionList = (row: IStockMovementArticle, cellId: keyof IStockMovementArticle, 
    opts: {value: string|number, name: string}[]) => {        

    const {articleFilterOption} = row;
    
    const enumsFilter = (refEnumItems.current ?? []).filter(x => x.parentEnumerationItemCode === articleFilterOption);

    return getAsOptions(enumsFilter ,Enum_ARTICLE_OPTION);
}


  const [headStockMovementArticleCells, setHeadStockMovementArticleCells]  = useState<HeadCell<IStockMovementArticle>[]>([]);
  useEffect(() => {
    setHeadStockMovementArticleCells([            
      {id:'id', label : t('Id'),  display: false, type: 'numeric', },
      {id:'articleName', label : t('Name'),  display: true, type: 'string', width: 50, isEditable: cellEditableArticle},
      {id:'productOptionName', label : t('Option'),  display: true, type: 'string', width: 40, },
      {id:'quantity', label : t('Qty'),  display: true, type: 'numeric', width: 10, 
          isEditable: cellEditableArticle },
    ]  )
  }, [t,i18n])

  const refAppendStockMovementArticles = useRef<(value: Partial<FieldArray<IStockMovement>> | Partial<FieldArray<IStockMovement>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateStockMovementArticle = useRef<(index: number,value: Partial<FieldArray<IStockMovement>> ) => void>(null);
  const refRemoveStockMovementArticle = useRef<(index: number ) => void>(null);
  
  const handleArticleDoubleClick = (event: React.MouseEvent<unknown>, index: number,row: IStockMovementArticle) => {
      const roleEntityProduct = roleEntities.find(re => re.entityName === 'Product');
      if(isFalsy(roleEntityProduct)) return;
          
      const product = getValues().stockMovementArticles.at(index);
      if(isFalsy(product)) return;
      
      navigate(`/product/${product.articleId}`);
    }

  const handleStockMovementArticleSelected = (event: React.MouseEvent<unknown>,index: number,row: IStockMovementArticle) => {
    setStockMovementArticleIndex(index);
  }

  const handleAddStockMovementArticle = (event: any) => {
    setOpenProductFilter(true);      
  }

  const handleProductOptionChoiceClick = async (productOption : IProductOption) => {
  
      const articleId = currentArticle2Add.id;
      const articleName = currentArticle2Add.name;
      
  
      if(getValues().stockMovementArticles.some(x => x.articleId === productOption.productId && x.productOptionId === productOption.id)) {
        //setOpenArticleOption(false);
        return;
      }
        
      (refAppendStockMovementArticles.current??emptyFunc)({...defaultStockMovementArticle, id: 0, stockMovementId: _id, 
        articleId, articleName, productOptionId: productOption.id, productOptionName: productOption.name, 
        articleFilterOption: '', option: '' });
    }

  const stockMovementArticleRowActionIcon = ( applicationQueryParameter: IStockMovementArticle) : ActionIconTableRow<IStockMovement,IStockMovementArticle> => {
  
    const res: ActionIconTableRow<IStockMovement,IStockMovementArticle> = {
      toolTip: 'remove',
      icon: RemoveCircleIcon,
      hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event : any,index: number, row: IStockMovementArticle) => {
        
        (refRemoveStockMovementArticle.current??emptyFunc)(index);            
      }
    }
    return res;
}

const handleMovementType = ( event: React.MouseEvent<HTMLElement>, movementType: MovementType ) => {
  
  if(movementType === null) return;
  setValue('type', movementType);
};


const refEnumItems = useRef<IEnumerationItem[]>();    
useEffect( () => {   
    refEnumItems.current = enumItems;
  
}, [enumItems]);


useEffect( () => {              
  setCurrentFormNameAtom(t('StockMovement'));  
  setCurrentBasicTextFilterProps(basicFilterStockMovement);
}, []);

  

  /********** This use effect call retrieve data wich will call refetch and _data will be updated. 
and the new useEffect will take place ********************/
useEffect( () => {
    // setCurrentFormName(t('Billing'));        
    setCurrentFormNameAtom(_id>0?`${t('StockMovement')} - # ${_id} # -`: t('StockMovement') );
    if(_id > 0)
      retrieveData('StockMovement',_id, refetch);  
  }, [_id] );

  useEffect( () => {   
    refArticleIndex.current = stockMovementArticleIndex;
  
  }, [stockMovementArticleIndex]);


useEffect( () => {
   
    if(_data && _data.id > 0) {
    reset(_data);
}
}, [_data]);

const newData = async (event: MouseEvent<HTMLButtonElement>) => {    
  _setId(0);           
  reset(defaultStockMovement);    
}

const saveData = async (event: MouseEvent<HTMLButtonElement>) => {   
  if(!checkEntitySaveAuthorization('StockMovement', _id)) {
    setIsSaveLoading(false);
       return;
  }

  const data = getValues(); 

  const {storeId, reference, description} = data;

  if(storeId <= 0) {
    enqueueSnackbar( t('Origin store and destination store must be specified'), { variant: 'warning',
      anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
    setIsSaveLoading(false);
    return;
  }

  
  if(reference.trim() === '' || description.trim() === '') {
      enqueueSnackbar( t('Reference is not specified'), { variant: 'warning',
        anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
      setIsSaveLoading(false);
      return;
    }

  
  if(data.stockMovementArticles.some( x => x.productOptionId <= 0 || x.quantity <= 0 )) {
    enqueueSnackbar( t('All articles must have option and quantity defined'), { variant: 'warning',
      anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
    setIsSaveLoading(false);
    return;
  }

  mutate(data);
}

const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
  openEntityActionDrawer('StockMovement', _id);
}

const printData = async (event: MouseEvent<HTMLButtonElement>) => {      
  openEntityPrintDrawer('StockMovement', _id);
}

const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {          
  queryClient.invalidateQueries(['StockMovement',_id]);        
  await retrieveData('StockMovement',_id, refetch);        
  reset(_data);             
}

  return (
        <FormProvider {...methods} >
          <Box sx={{ mx: 0.1 }}>
            <Grid container rowSpacing={4} columnSpacing={1}>
              <Grid item xs={12} md={5} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >                        
                <Stack flexDirection='column'  >
                    <Box sx={{ mt: 1, width: '100%' }} >
                        <Button id='btnNew' onClick={newData} sx={ {display:'none'}}  />                                  
                        <Button id='btnSave' onClick={saveData} sx={ {display:'none'}}  />
                        <Button id='btnAction' onClick={actionData} sx={ {display:'none'}}  />                                                              
                        <Button id='btnAfterAction' onClick={afterAction} sx={ {display:'none'}}  />

                        <Button id='btnPrint' onClick={printData} sx={ {display:'none'}}  />

                        
                        <TextField sx={{width:'calc(70% - 8px)'}} id="reference" label={t('Reference')} 
                            inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } } autoFocus {...register('reference')} />
                        <Controller control={control}
                          name='stockMovementDate' 
                          render={({ field: { onChange, onBlur, value, ref } }) => (
                            <DatePicker label={t('Date')} 
                              onChange={onChange}                      
                              value={new Date(value)}
                              slotProps={{ textField: { sx: {width:'calc(30% - 8px)'}  }} }
                              //renderInput={(params) => <TextField {...params} sx={{width:'calc(30% - 8px)'}} />}
                            /> )}
                        />
                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <TextField sx={{width:'calc(100% - 8px)'}} id="description" label={t('Description')} {...register('description')} />
                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <ToggleButtonGroup value={watchType} exclusive key={watchType} size="small" onChange={handleMovementType} aria-label="text alignment" fullWidth >
                        <ToggleButton value="in" aria-label="centered">
                          {t('Movement in')}
                        </ToggleButton>
                        <ToggleButton value="out" aria-label="centered">
                          {t('Movement out')}
                        </ToggleButton>                                  
                      </ToggleButtonGroup>                                                             
                    </Box>
                    <Box sx={{ mt: 0.25, width: '100%' }} > 
                      <Typography variant="h6" id="tableTitle" color="primary" noWrap 
                            sx={{...typographyGroupBoxStyling}}>
                        {`${t('Store')} :: <==> ${t(('Purpose'))}`}
                      </Typography>                                                       
                    </Box>   
                    <Box sx={{ mt: 1, width: '100%' }} > 
                      <Controller 
                          name='storeId' control={control}                                     
                          render={ ({field: {onChange, value}}) => (
                            <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="storeId"
                              label={`:: ${t('Store')} =>`} inputProps={ {readOnly: false}} >
                              {stores && stores.map( 
                                (x,idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem> )
                              }
                            </TextField>
                          )}
                        />
                        <Controller 
                          name='movementPurpose' control={control}                                     
                          render={ ({field: {onChange, value}}) => (
                            <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="movementPurpose"
                              label={`:: ${t('Purpose')} =>`} inputProps={ {readOnly: false}} >
                              {enumItems && enumItems.filter( e => 
                                      e.enumerationCode === ( watchType==='in' ? 
                                        Enum_STOCK_MOVEMENT_PURPOSE_IN: Enum_STOCK_MOVEMENT_PURPOSE_OUT) ).map( 
                                  (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                }
                            </TextField>
                          )}
                        />
                    </Box> 
                    
                    <Box sx={{ mt: 0.25, width: '100%' }} > 
                      <Typography variant="h6" id="tableTitle" color="primary" noWrap 
                            sx={{...typographyGroupBoxStyling}}>
                        {`${t(('Status of stock transfer'))} `}
                      </Typography>                                                       
                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller control={control}
                          name='issueDate' 
                          render={({ field: { onChange, onBlur, value, ref } }) => (
                            <DatePicker label={t('Issue date')} 
                              onChange={onChange} readOnly disableOpenPicker                     
                              value={new Date(value)}
                              slotProps={{ textField: { sx: {width:'calc(30% - 8px)'}  }} }
                              //renderInput={(params) => <TextField {...params} sx={{width:'calc(30% - 8px)'}} />}
                            /> )}
                      />
                      <Controller 
                          name={`status`}
                          control={control}
                          render={ ({field: {onChange, value}}) => (
                            <TextField select onChange={onChange} value={value} sx={{width:'calc(70% - 8px)'}} id="status"
                                label={t('Status')} inputProps={ {readOnly: true }} >
                                {enumItems && enumItems.filter( e => 
                                      e.enumerationCode === Enum_OPERATION_STATUS ).map( 
                                  (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                }
                              </TextField>
                          )}
                      />
                      
                    </Box>
                    <Box sx={{ mt: 1, width: '100%' }} >
                      <Controller control={control}
                            name='statusDate' 
                            render={({ field: { onChange, onBlur, value, ref } }) => (
                              <DatePicker label={t('Status date')} 
                                onChange={onChange} readOnly disableOpenPicker                     
                                value={new Date(value)}
                                slotProps={{ textField: { sx: {width:'calc(30% - 8px)'}  }} }
                                //renderInput={(params) => <TextField {...params} sx={{width:'calc(30% - 8px)'}} />}
                              /> )}
                        />
                        <Controller 
                            name='statusPurpose' control={control}                                     
                            render={ ({field: {onChange, value}}) => (
                              <TextField select onChange={onChange} value={value} sx={{width:'calc(70% - 8px)'}} id="status"
                                label={t('Status purpose')} inputProps={ {readOnly: true}} >
                                {enumItems && enumItems.filter( e => 
                                      e.enumerationCode === Enum_STOCK_TRANSFER_STATUS_PURPOSE ).map( 
                                  (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                }
                              </TextField>
                            )}
                        />
                    </Box>
                </Stack>                        
              </Grid>                   
              <Grid item xs={12}  md={7} component={Paper} >
                  <Stack flexDirection='column'> 
                    
                    <Box key={`${'watchFileName'} - key ${getValues().stockMovementArticles.map(x => x.quantity)}`} sx={{ mt: 1, width: '100%' }} >
                      <ArrayFieldTableEx<IStockMovement,IStockMovementArticle,'id'> 
                        mainObject={getValues()} fieldKey='id' 
                        headCells={headStockMovementArticleCells} rowsPathName={`stockMovementArticles` }
                        title={t('_Articles')} rowActionIcon={stockMovementArticleRowActionIcon}  
                        onRowSelected={handleStockMovementArticleSelected}
                                            
                        refAppend={refAppendStockMovementArticles as MutableRefObject<(value: Partial<FieldArray<IStockMovement>> | Partial<FieldArray<IStockMovement>>[], options?: FieldArrayMethodProps) => void>}
                        refUpdate={refUpdateStockMovementArticle as MutableRefObject<(index: number,value: Partial<FieldArray<IStockMovement>>) => void>}
                        refRemove={refRemoveStockMovementArticle as MutableRefObject<(index: number) => void>}

                        onRowDoubleClick={handleArticleDoubleClick}  
                        //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                        //displayMore={undefined}
                        toolbarActions={[
                        { toolTip: `${t('Add')}...`, onClickIcon: handleAddStockMovementArticle ,icon: AddCircleIcon,  },
                        
                        ]}
                      />  
                      { openProductFilter && <FormDialog open={openProductFilter} maxWidth='md'
                        okText={t('OK')} cancelText={t('Cancel')} title={t('Product filter')} onCancel={()=> {setOpenProductFilter(false);}} 
                        onClose={()=> {setOpenProductFilter(false);}} onOk={()=> {setOpenProductFilter(false);}}  >
                            <BasicTextFilterForm<IProduct> {...basicFilterProduct } />
                        </FormDialog> }
                        { showProductOption && <FormDialog open={showProductOption} maxWidth='xs' height='50vh'
                                okText={t('Close')} cancelText='' title='' onCancel={()=> {}} 
                                onClose={()=> {setShowProductOption(false);}} onOk={()=> {setShowProductOption(false);}}  >
                                    <BasicButtonList<IProductOption> 
                                        items={currentProductOptions} 
                                        icon={BoltIcon} 
                                        onItemClick={handleProductOptionChoiceClick} 
                                        valueKey={'id'} displayKey={'name'} title={t('Product options')} />
                        </FormDialog> }
                                             
                    </Box>
                      
                  </Stack> 
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
  )
}
